import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { KyRequestOptions } from '@vestigas/data';

import { HTTPError, isAbortError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type SupplierLetterOverridesResponse } from './types';
import { zodSupplierLetterOverridesResponseSchema } from './validations';

/**
 * Fetches the list of child companies that have their own supplier letter, for a given CompanyAccount.
 * Returns `null` when no overrides are configured (404).
 *
 * @param companyAccountId - The ID of the company account.
 * @param options - Optional fetch options (e.g. AbortSignal).
 * @returns The overrides response containing child companies with a letter, or null when none exist.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_overrides_vadmin_company_accounts__company_account_id__supplier_letter_overrides_get
 */
export const fetchCompanyAccountSupplierLetterOverrides = async (
  companyAccountId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(
        ENDPOINT.COMPANY.ACCOUNT.SUPPLIER_LETTER.OVERRIDES(companyAccountId),
        {
          signal: options?.signal,
          validate: { response: zodSupplierLetterOverridesResponseSchema },
        },
      )
      .json<SupplierLetterOverridesResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      return null;
    }

    // A 404 means no overrides are configured. This is an expected state and
    // should fail silently without an error toast.
    if (error instanceof HTTPError && error.response.status === 404) {
      return null;
    }

    log.error({
      message: 'Error fetching company account supplier letter overrides',
      error,
    });

    ToastService.error(
      i18n.t(
        ($) =>
          $.companyAccount.fetchCompanyAccountSupplierLetterOverrides.error
            .generic,
        { ns: 'data' },
      ),
    );

    throw error;
  }
};
